import type {
  Capacity,
  ScalingConfig,
  ScalingMetric,
  StaffAssignmentConfig,
} from '@repo/contracts';

import type { ShiftSet } from '@/types/template';

/**
 * Resolves the number of staff a staffing config asks for. A `specific`
 * assignment is always a single named person; `open`/`automatic` carry an
 * explicit `count`.
 */
export function resolveStaffCount(staffing: StaffAssignmentConfig): number {
  return staffing.method === 'specific' ? 1 : staffing.count;
}

/**
 * Forces a scaling shift's model metric to the template-level `metric` (the UI
 * has no per-shift metric). A no-op when the shift has no scaling or is
 * `specific`. Used by the mapper and when the template metric changes.
 */
export function withScalingMetric(shift: ShiftSet, metric: ScalingMetric): ShiftSet {
  if (shift.staffing.method === 'specific') return shift;
  const { scaling } = shift.staffing;
  if (!scaling || scaling.model.metric === metric) return shift;
  return {
    ...shift,
    staffing: { ...shift.staffing, scaling: { ...scaling, model: { ...scaling.model, metric } } },
  };
}

/**
 * Resolves how many staff instances a shift represents for preview/grouping.
 */
export function resolveInstanceCount(shift: ShiftSet): number {
  return resolveStaffCount(shift.staffing);
}

/**
 * Resolves a shift's head count for a specific ISO day-of-week (1 = Mon … 7 =
 * Sun). The preview pipeline takes one of these so counts can vary per day once
 * demand scaling is in play; the default ignores the day.
 */
export type DayCountResolver = (shift: ShiftSet, dayOfWeek: number) => number;

/** Default {@link DayCountResolver}: the day-independent fixed staffing count. */
export function fixedDayCount(shift: ShiftSet): number {
  return resolveInstanceCount(shift);
}

/**
 * Per-day baseline and forecast capacity for one date, mirroring rotagen's
 * `DayCapacity` (apps/rotagen/src/core/scaling/shift-scaling.utils.ts). Either
 * side may be absent; the scaler falls back to the literal count when the
 * required metric is unresolved.
 */
export interface DayCapacity {
  baseline?: Capacity;
  forecast?: Capacity;
}

/**
 * Resolves the demand-scaled instance count for a shift on a single day, so
 * the live preview shows the same per-day head count rotagen will generate.
 *
 * Mirrors rotagen's `scaleShiftCount`: a `specific` shift is always one
 * instance; otherwise it starts from the base staffing count and, when a
 * `scaling` config is present, applies the linear or ratio model against the
 * day's capacity and clamps to the config's `min`/`max`. Falls back to the base
 * count when scaling is absent or the metric is missing on either side.
 */
export function resolveScaledInstanceCount(shift: ShiftSet, capacity: DayCapacity): number {
  if (shift.staffing.method === 'specific') return 1;

  const baseCount = shift.staffing.count;
  const config = shift.staffing.scaling;
  if (config === undefined) return baseCount;

  return clampToConfig(config, applyScalingModel(config, capacity, baseCount));
}

/** Dispatches to the configured scaling model. */
function applyScalingModel(
  config: ScalingConfig,
  capacity: DayCapacity,
  baseCount: number,
): number {
  switch (config.model.type) {
    case 'linear': {
      const { baseline, forecast } = metricPair(capacity, config.model.metric);
      if (baseline === undefined || forecast === undefined) return baseCount;
      return Math.round(baseCount * (forecast / baseline));
    }
    case 'ratio': {
      const { baseline, forecast } = metricPair(capacity, config.model.metric);
      if (baseline === undefined || forecast === undefined) return baseCount;
      const extras = Math.trunc((forecast - baseline) / config.model.unitsPerStaff);
      return Math.max(0, baseCount + extras);
    }
  }
}

/** Reads the baseline and forecast values for a metric off the day's capacity. */
function metricPair(capacity: DayCapacity, metric: keyof Capacity) {
  return { baseline: capacity.baseline?.[metric], forecast: capacity.forecast?.[metric] };
}

/** Clamps a count to the config's optional `min`/`max` caps. */
function clampToConfig(config: ScalingConfig, count: number): number {
  let clamped = count;
  if (config.min !== undefined) clamped = Math.max(clamped, config.min);
  if (config.max !== undefined) clamped = Math.min(clamped, config.max);
  return clamped;
}
